import {
  isValidElement,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';
import * as stylex from '@stylexjs/stylex';
import {
  Badge,
  HiddenLegend,
  MenuV2,
  PopoverOutlet,
  SegmentedControl,
  Text,
  TextareaPrimitive,
} from '@figma/fpl-components';
import { Icon16ChevronDown } from '@figma/fpl-icons';
import { borderRadius, color, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { I18N_KEYS } from '@/language/keys';
import { resolveMenuItemIcon } from '@/components/Recipe/FlowComponents/LeftPanelSections/NodeIconMap';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { defaultModelFor } from '@/components/Generate/generateModelAccess';
import { GenerateCreditsBanner } from '@/components/Generate/GenerateCreditsBanner';
import {
  ALLOW_EXPENSIVE_RUN_KEY,
  EXPENSIVE_RUN_CREDITS,
  ExpensiveRunModal,
} from '@/components/Common/ExpensiveRunModal/ExpensiveRunModal';
import { settingsFingerprint, toRunParams } from '@/api/generate.api';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useGeneratePrice } from '@/queries/useGeneratePage';
import { CreditsContext } from '@/services/CreditsContext';
import { getFilteredAcceptedFileTypes } from '@/utils/fileTypes';
import { getMediaPanelDragFiles } from '@/utils/mediaPanelDrag.utils';
import { ComposerFooter } from './ComposerFooter';
import { AddButton, InputBanner, InputRow, type OpenPanel } from './ComposerInputs';
import { toMediaAsset, typesFor, type InputSlot } from './inputRules';
import { useComposerInputs, type InputTarget } from './useComposerInputs';
import { isMediaDrag, useDragHighlight, usePageFileDropGuard } from './useMediaDrop';
import { MenuTrigger, MenuTriggerTooltip, type MenuPlacement } from './MenuTrigger';
import { useSavedModelSettings, type SavedSettings } from './useSavedModelSettings';
import type {
  GenerateInputs,
  GenerateMediaType,
  GenerateMode,
  GenerateModel,
  GenerateParamValue,
  GenerateRequest,
} from '@/api/generate.api';
import type { MediaAsset } from '@/types/api/assets';

const NO_SLUGS: ReadonlySet<string> = new Set();
const NO_RESULTS: readonly MediaAsset[] = [];

/** The file picker's `accept` list for the types a slot takes, the same types the canvas accepts. */
const acceptFor = (types: readonly GenerateMediaType[]): string =>
  types
    .flatMap((type) =>
      Object.entries(getFilteredAcceptedFileTypes(type)).flatMap(([mime, extensions]) => [mime, ...extensions]),
    )
    .join(',');
const GENERATE_MODES: readonly GenerateMode[] = ['image', 'video'];

export type ComposerDraft = {
  modelSlug: string;
  params: Readonly<Record<string, GenerateParamValue>>;
  count: number;
};

export type DraftPatch = Partial<Omit<ComposerDraft, 'modelSlug'>>;

type GenerateComposerProps = {
  models: readonly GenerateModel[];
  /** Listed but can't be picked, such as video models on the free plan. */
  lockedModelSlugs?: ReadonlySet<string>;
  /** Marked New in the backoffice. */
  newModelSlugs?: ReadonlySet<string>;
  /** Null when the URL has no model, or the slug is not in the catalog. */
  model: GenerateModel | null;
  /** The tab shown while no model is picked, such as from the Toolkit's Videos card. */
  requestedMode?: GenerateMode;
  /** The URL names a model the catalog does not have, or one this user can't pick. */
  modelMissing: boolean;
  /** The plan lets this user pick Auto duration on `model`. */
  canUseAutoDuration?: boolean;
  signedIn: boolean;
  /** Whose saved model settings to load. Without one, settings last only until the page reloads. */
  userId?: string;
  onModelChange: (modelSlug: string) => void;
  /** Same handoff as the tool page: sign in, then return to this generate URL. */
  onSignIn: () => void;
  /** Prompt sits at the bottom of the page, so menus open upward. */
  docked?: boolean;
  /** A generation is in flight. The primary button stays disabled until it finishes. */
  generating?: boolean;
  /** Signed-in Generate. Called with the prompt and the current model settings. */
  onGenerate?: (request: GenerateRequest) => void;
  /** Set while outputs are being made. Generate turns into Cancel, which stops them. */
  onCancel?: () => void;
  /** The results on the page, newest first, which the add panel offers as references. */
  gallery?: readonly MediaAsset[];
};

/** Where each primary setting of `model` starts before the user changes it. */
const defaultParams = (model: GenerateModel, canUseAutoDuration: boolean): Record<string, GenerateParamValue> =>
  Object.fromEntries(
    model.params.map((param) => [
      param.key,
      param.type === 'integer' && param.auto && param.startsAuto && canUseAutoDuration
        ? param.auto
        : param.defaultValue,
    ]),
  );

/** Credits for `count` outputs, rounded like the tool page's total. */
const totalCredits = (unitCost: number | null | undefined, count: number) =>
  unitCost === null || unitCost === undefined ? undefined : Math.round(unitCost * count * 100) / 100;

/** The runs count, for the model it was set on. Any other model starts at 1. */
type CountChoice = { modelSlug: string; count: number };

/**
 * The saved values to use for `model`: none when its settings changed since they were saved. A saved Auto
 * duration is also dropped when the plan doesn't allow Auto, for example after a downgrade.
 */
const savedValuesFor = (
  model: GenerateModel,
  saved: SavedSettings | undefined,
  canUseAutoDuration: boolean,
): Readonly<Record<string, GenerateParamValue>> => {
  if (!saved || saved.fingerprint !== settingsFingerprint(model)) {
    return {};
  }
  if (canUseAutoDuration) {
    return saved.values;
  }
  const autoValues = new Map(
    model.params.flatMap((param) => (param.type === 'integer' && param.auto ? [[param.key, param.auto]] : [])),
  );
  return Object.fromEntries(Object.entries(saved.values).filter(([key, value]) => autoValues.get(key) !== value));
};

/**
 * The draft for `model`: its defaults, with the saved values on top. While node definitions load, the catalog
 * shows the model with no params under the same slug, so its fingerprint doesn't match yet and nothing saved
 * shows. The saved values stay, and show once the params arrive.
 */
const draftFor = (
  model: GenerateModel,
  saved: SavedSettings | undefined,
  countChoice: CountChoice | null,
  canUseAutoDuration: boolean,
): ComposerDraft => ({
  modelSlug: model.slug,
  params: { ...defaultParams(model, canUseAutoDuration), ...savedValuesFor(model, saved, canUseAutoDuration) },
  count: countChoice?.modelSlug === model.slug ? countChoice.count : 1,
});

/**
 * Prompt card for `/generate`. The field value is the prompt a generation call will send.
 * Signed-in Generate submits that prompt. Signed-out visitors get the tool page's sign-in action.
 */
export function GenerateComposer({
  models,
  lockedModelSlugs = NO_SLUGS,
  newModelSlugs = NO_SLUGS,
  model,
  requestedMode = 'image',
  modelMissing,
  canUseAutoDuration = false,
  signedIn,
  userId,
  onModelChange,
  onSignIn,
  docked = false,
  generating = false,
  onGenerate,
  onCancel,
  gallery = NO_RESULTS,
}: GenerateComposerProps) {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const inputs = useComposerInputs(model);
  const [openPanel, setOpenPanel] = useState<OpenPanel | null>(null);
  const { over: dragOver, setOver: setDragOver, onDragLeave: handleDragLeave } = useDragHighlight();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // The slot the file picker fills. Only one picker is open at a time.
  const [uploadSlot, setUploadSlot] = useState<InputSlot>('reference');
  // Auto can't fit a video to a frame, the same as on the canvas.
  const autoAllowed = canUseAutoDuration && !inputs.hasFrame;
  const { savedSettings, saveModelSettings } = useSavedModelSettings(userId);
  // Not saved with the settings: a count that comes back by itself could run more outputs than the user meant.
  const [countChoice, setCountChoice] = useState<CountChoice | null>(null);
  const draft = model ? draftFor(model, savedSettings[model.slug], countChoice, autoAllowed) : null;
  const runInput = useMemo(
    () => (model && draft ? { ...model.baseInput, ...toRunParams(model.params, draft.params) } : null),
    [draft, model],
  );
  const price = useGeneratePrice(signedIn && model && runInput ? { modelId: model.id, input: runInput } : null);
  const { fetchUnitCost } = price;
  const { credits } = useContext(CreditsContext);
  const totalCost = draft ? totalCredits(price.unitCost, draft.count) : undefined;
  const notEnoughCredits = totalCost !== undefined && typeof credits === 'number' && totalCost > credits;
  // True from a press until its price arrives, so a second press doesn't start a second Generate.
  const [pricing, setPricing] = useState(false);
  // A Generate waiting for the user to confirm its high cost.
  const [expensiveRun, setExpensiveRun] = useState<{ request: GenerateRequest; total: number } | null>(null);
  const { getItem: getAllowExpensiveRun } = useLocalStorage<boolean>(ALLOW_EXPENSIVE_RUN_KEY);
  const mode = model?.mode ?? requestedMode;
  const lockedModes = GENERATE_MODES.filter(
    (target) =>
      models.some((option) => option.mode === target) &&
      models.every((option) => option.mode !== target || lockedModelSlugs.has(option.slug)),
  );
  // no model opens by itself when every model of the requested tab is locked, so the reason shows without a hover
  const modeLocked = !model && !modelMissing && lockedModes.includes(mode);

  const updateDraft = useCallback(
    (patch: DraftPatch) => {
      if (!model) return;
      if (patch.count !== undefined) {
        setCountChoice({ modelSlug: model.slug, count: patch.count });
      }
      if (patch.params) {
        // Only what differs from the default is saved, so a setting the user left alone follows the definition.
        const defaults: Record<string, unknown> = {
          ...Object.fromEntries(model.moreParams.map((param) => [param.key, param.defaultValue])),
          ...defaultParams(model, autoAllowed),
        };
        const changed = Object.fromEntries(
          Object.entries(patch.params).filter(([key, value]) => !isEqual(value, defaults[key])),
        );
        // A frame hides a saved Auto duration. It stays saved until the user picks a duration, so it comes back
        // once the frame is removed.
        const saved = savedValuesFor(model, savedSettings[model.slug], canUseAutoDuration);
        const hiddenAuto = Object.fromEntries(
          model.params.flatMap((param) =>
            !autoAllowed &&
            param.type === 'integer' &&
            param.auto &&
            saved[param.key] === param.auto &&
            patch.params?.[param.key] === draft?.params[param.key]
              ? [[param.key, param.auto]]
              : [],
          ),
        );
        saveModelSettings(model.slug, {
          fingerprint: settingsFingerprint(model),
          values: { ...changed, ...hiddenAuto },
        });
      }
    },
    [autoAllowed, canUseAutoDuration, draft, model, saveModelSettings, savedSettings],
  );

  const { addAssets, addFiles, runInputs } = inputs;
  const inputSpec: GenerateInputs = model?.inputs ?? {};
  const takesInputs = Boolean(inputSpec.references || inputSpec.frames);

  const handleConfirm = useCallback(
    (assets: readonly MediaAsset[], slot: InputSlot) => addAssets(assets, slot),
    [addAssets],
  );

  // The picker lists only the types the slot takes, and the slot's own check still runs on what comes back.
  const handleUpload = useCallback((slot: InputSlot) => {
    // The input's `accept` and `multiple` follow the slot, so they must render before the picker opens. The
    // picker must still open inside the click, or a browser like Safari blocks it.
    flushSync(() => setUploadSlot(slot));
    fileInputRef.current?.click();
  }, []);

  const handleFilesPicked = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      // Clear the input so picking the same file again after removing it still fires `change`.
      event.target.value = '';
      if (files.length > 0) {
        addFiles(files, uploadSlot, true);
      }
    },
    [addFiles, uploadSlot],
  );

  /** A drop on the box, or on one slot, which then gets what was dropped. */
  const dropInto = useCallback(
    (dataTransfer: DataTransfer, target: InputTarget) => {
      if (!signedIn) {
        onSignIn();
        return;
      }
      // A dragged result carries its asset, so it needs no upload. Check it first: the browser adds a file to
      // an image drag too.
      const results = getMediaPanelDragFiles(dataTransfer);
      if (results) {
        addAssets(
          results.flatMap((result) => toMediaAsset(result) ?? []),
          target,
        );
        return;
      }
      const files = Array.from(dataTransfer.files);
      if (files.length > 0) {
        addFiles(files, target, false);
      }
    },
    [addAssets, addFiles, onSignIn, signedIn],
  );

  // A media drag is never let through to the prompt, which would paste a result's link as text. With no model
  // to take it, the drop just does nothing.
  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!isMediaDrag(event.dataTransfer)) {
        return;
      }
      event.preventDefault();
      if (takesInputs) {
        setDragOver(true);
      } else {
        event.dataTransfer.dropEffect = 'none';
      }
    },
    [setDragOver, takesInputs],
  );

  // Files pasted into the prompt are handled like files dropped on it. Text pastes as usual.
  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(event.clipboardData.files);
      if (!takesInputs || files.length === 0) {
        return;
      }
      event.preventDefault();
      if (!signedIn) {
        onSignIn();
        return;
      }
      addFiles(files, 'drop', false);
    },
    [addFiles, onSignIn, signedIn, takesInputs],
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      // a slot already took it
      if (event.defaultPrevented || !isMediaDrag(event.dataTransfer)) {
        return;
      }
      event.preventDefault();
      setDragOver(false);
      if (takesInputs) {
        dropInto(event.dataTransfer, 'drop');
      }
    },
    [dropInto, setDragOver, takesInputs],
  );

  usePageFileDropGuard();

  const canGenerate =
    prompt.trim().length > 0 && Boolean(model) && !notEnoughCredits && !inputs.blocking && !inputs.firstFrameNeeded;

  const handlePrimaryClick = useCallback(() => {
    if (!signedIn) {
      onSignIn();
      return;
    }
    if (!model || !draft || !runInput || !canGenerate || generating || pricing) return;
    const request: GenerateRequest = {
      modelId: model.id,
      prompt: prompt.trim(),
      mode: model.mode,
      params: runInput,
      inputs: runInputs,
      count: draft.count,
    };
    // Generate stays on while the price loads, so the press waits for it before checking the balance.
    // A price the server can't give doesn't stop the run: the server prices and checks it again anyway.
    setPricing(true);
    void fetchUnitCost().then((unitCost) => {
      setPricing(false);
      const total = totalCredits(unitCost, request.count);
      if (total === undefined) {
        onGenerate?.(request);
        return;
      }
      if (typeof credits === 'number' && total > credits) {
        return;
      }
      if (total >= EXPENSIVE_RUN_CREDITS && !getAllowExpensiveRun()) {
        setExpensiveRun({ request, total });
        return;
      }
      onGenerate?.(request);
    });
  }, [
    canGenerate,
    credits,
    draft,
    fetchUnitCost,
    generating,
    getAllowExpensiveRun,
    model,
    onGenerate,
    onSignIn,
    pricing,
    prompt,
    runInput,
    runInputs,
    signedIn,
  ]);

  const handleExpensiveRunConfirm = useCallback(() => {
    if (expensiveRun) {
      onGenerate?.(expensiveRun.request);
    }
    setExpensiveRun(null);
  }, [expensiveRun, onGenerate]);
  const handleExpensiveRunClose = useCallback(() => setExpensiveRun(null), []);

  // Cmd+Enter (Ctrl+Enter on Windows) does what the primary button does. Plain Enter still adds a line.
  const handlePromptKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      // `isComposing` is an Enter that confirms text in an input method, such as for Japanese.
      if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey) || event.nativeEvent.isComposing) {
        return;
      }
      event.preventDefault();
      handlePrimaryClick();
    },
    [handlePrimaryClick],
  );

  const panelProps = {
    inputs: inputSpec,
    room: inputs.room,
    gallery,
    attached: inputs.attached,
    openPanel,
    onOpenPanel: setOpenPanel,
    onSignIn: signedIn ? undefined : onSignIn,
    onConfirm: handleConfirm,
    onUpload: handleUpload,
    onDropInto: dropInto,
  };

  return (
    <PopoverOutlet>
      {notEnoughCredits && model && (
        <div {...stylex.props(styles.creditsBanner)}>
          <GenerateCreditsBanner modelName={model.modelName} />
        </div>
      )}
      <InputBanner
        notice={inputs.notice}
        firstFrameNeeded={inputs.firstFrameNeeded}
        inputs={inputSpec}
        onDismiss={inputs.clearNotice}
      />
      <input
        ref={fileInputRef}
        hidden
        type="file"
        aria-hidden
        tabIndex={-1}
        accept={acceptFor(typesFor(inputSpec, uploadSlot))}
        multiple={uploadSlot === 'reference'}
        onChange={handleFilesPicked}
      />
      <div
        {...stylex.props(styles.card, dragOver && styles.cardDragOver)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div {...stylex.props(styles.header)}>
          <ModeControl
            models={models.filter((option) => !lockedModelSlugs.has(option.slug))}
            lockedModes={lockedModes}
            mode={mode}
            onModelChange={onModelChange}
          />
          <ModelMenu
            models={models.filter((option) => option.mode === mode)}
            lockedModelSlugs={lockedModelSlugs}
            newModelSlugs={newModelSlugs}
            model={model}
            placement={docked ? 'top span-right' : 'bottom span-right'}
            onChange={onModelChange}
          />
        </div>

        {modelMissing && (
          <Text.p color="secondary" role="status" {...stylex.props(styles.note)}>
            {t(I18N_KEYS.GENERATE_PAGE.MODEL_NOT_FOUND)}
          </Text.p>
        )}

        {modeLocked && (
          <Text.p color="secondary" role="status" {...stylex.props(styles.note)}>
            {t(I18N_KEYS.GENERATE_PAGE.PAID_PLANS_ONLY)}
          </Text.p>
        )}

        <InputRow {...panelProps} onRemove={inputs.remove} />

        <TextareaPrimitive
          aria-label={t(I18N_KEYS.GENERATE_PAGE.PROMPT_LABEL)}
          placeholder={t(I18N_KEYS.GENERATE_PAGE.PROMPT_PLACEHOLDER)}
          value={prompt}
          onChange={setPrompt}
          onKeyDown={handlePromptKeyDown}
          onPaste={handlePaste}
          rows={2}
          expandable
          maxHeight={280}
          {...stylex.props(styles.input)}
        />

        <ComposerFooter
          model={model}
          draft={draft}
          canUseAutoDuration={autoAllowed}
          signedIn={signedIn}
          canGenerate={canGenerate}
          generating={generating || pricing}
          totalCost={totalCost}
          costLoading={price.isLoading}
          addButton={<AddButton {...panelProps} />}
          onDraftChange={updateDraft}
          onPrimaryClick={handlePrimaryClick}
          onCancel={onCancel}
        />
      </div>
      {expensiveRun && (
        <ExpensiveRunModal
          open
          cost={expensiveRun.total}
          onConfirm={handleExpensiveRunConfirm}
          onClose={handleExpensiveRunClose}
        />
      )}
    </PopoverOutlet>
  );
}

/**
 * Image / video toggle. The mode is the selected model's, so picking the other mode opens its default model,
 * and the model menu lists only that mode's models. A mode with no models stays visible but read-only.
 * `models` leaves out locked ones, so a mode whose models are all locked is read-only too.
 */
function ModeControl({
  models,
  lockedModes,
  mode,
  onModelChange,
}: {
  models: readonly GenerateModel[];
  /** Modes whose every model is locked, which say why on hover. */
  lockedModes: readonly GenerateMode[];
  mode: GenerateMode;
  onModelChange: (modelSlug: string) => void;
}) {
  const { t } = useTranslation();
  const hasMode = (target: GenerateMode) => models.some((option) => option.mode === target);
  // The option's label takes only text, so the tooltip wraps the whole option, sized like it.
  // A locked mode says why instead of what it is.
  const withTooltip = (target: GenerateMode, option: ReactElement) => (
    <Tooltip
      title={
        lockedModes.includes(target)
          ? t(I18N_KEYS.GENERATE_PAGE.PAID_PLANS_ONLY)
          : t(target === 'image' ? I18N_KEYS.GENERATE_PAGE.IMAGE_OUTPUT : I18N_KEYS.GENERATE_PAGE.VIDEO_OUTPUT)
      }
    >
      <span {...stylex.props(styles.modeOption)}>{option}</span>
    </Tooltip>
  );
  const handleChange = useCallback(
    (next: string) => {
      const nextMode = GENERATE_MODES.find((option) => option === next);
      const nextModel = nextMode && defaultModelFor(models, nextMode);
      if (nextModel) onModelChange(nextModel.slug);
    },
    [models, onModelChange],
  );

  return (
    <SegmentedControl.Root
      legend={<HiddenLegend>{t(I18N_KEYS.GENERATE_PAGE.OUTPUT_TYPE)}</HiddenLegend>}
      value={mode}
      onChange={handleChange}
    >
      {withTooltip(
        'image',
        <SegmentedControl.TextOption
          value="image"
          label={t(I18N_KEYS.GENERATE_PAGE.IMAGE)}
          readonly={hasMode('image') ? undefined : true}
        />,
      )}
      {withTooltip(
        'video',
        <SegmentedControl.TextOption
          value="video"
          label={t(I18N_KEYS.GENERATE_PAGE.VIDEO)}
          readonly={hasMode('video') ? undefined : true}
        />,
      )}
    </SegmentedControl.Root>
  );
}

function ModelMenu({
  models,
  lockedModelSlugs,
  newModelSlugs,
  model,
  placement,
  onChange,
}: {
  models: readonly GenerateModel[];
  lockedModelSlugs: ReadonlySet<string>;
  newModelSlugs: ReadonlySet<string>;
  model: GenerateModel | null;
  placement: MenuPlacement;
  onChange: (modelSlug: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { manager, getTriggerProps } = MenuV2.useMenu({ open, onOpenChange: setOpen });

  return (
    <>
      <MenuTriggerTooltip title={t(I18N_KEYS.GENERATE_PAGE.MODEL_SELECTION)} menuOpen={open}>
        <MenuTrigger
          icon={model ? <ModelIcon model={model} /> : undefined}
          {...getTriggerProps()}
          aria-label={t(I18N_KEYS.GENERATE_PAGE.MODEL)}
        >
          {model?.name ?? t(I18N_KEYS.GENERATE_PAGE.SELECT_MODEL)}
          <Icon16ChevronDown />
        </MenuTrigger>
      </MenuTriggerTooltip>
      <MenuV2.Root
        manager={manager}
        aria-label={t(I18N_KEYS.GENERATE_PAGE.MODEL)}
        position={{ area: placement }}
        colorTheme={{ brand: 'weave', mode: 'dark' }}
      >
        <MenuV2.RadioGroup aria-label={t(I18N_KEYS.GENERATE_PAGE.MODEL)} value={model?.slug} onChange={onChange}>
          {models.map((option) => {
            const locked = lockedModelSlugs.has(option.slug);
            // a locked model shows only Upgrade, since that's what the user can act on
            const tag = locked
              ? t(I18N_KEYS.GENERATE_PAGE.UPGRADE_TAG)
              : newModelSlugs.has(option.slug)
                ? t(I18N_KEYS.GENERATE_PAGE.NEW_TAG)
                : undefined;
            return (
              <MenuV2.RadioGroupItem
                key={option.slug}
                value={option.slug}
                lead={<ModelIcon model={option} />}
                trail={tag ? <Badge>{tag}</Badge> : undefined}
                disabled={locked}
              >
                {option.name}
              </MenuV2.RadioGroupItem>
            );
          })}
        </MenuV2.RadioGroup>
      </MenuV2.Root>
    </>
  );
}

/** Same lookup as the nodes menu, keyed by the definition's `icon`. */
function ModelIcon({ model }: { model: GenerateModel }) {
  const icon = resolveMenuItemIcon({ icon: model.icon, name: model.id });
  return isValidElement(icon) ? icon : null;
}

const styles = stylex.create({
  cardDragOver: {
    borderColor: color.borderSelected,
  },
  card: {
    backgroundColor: color.bg,
    borderColor: color.border,
    borderRadius: borderRadius.large,
    borderStyle: 'solid',
    borderWidth: '1px',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '768px',
    width: '100%',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: spacer.spacer2,
    paddingBottom: spacer.spacer2,
    paddingLeft: spacer.spacer25,
    paddingRight: spacer.spacer3,
    paddingTop: spacer.spacer25,
  },
  creditsBanner: {
    marginBottom: spacer.spacer2,
    maxWidth: '768px',
    width: '100%',
  },
  modeOption: {
    display: 'flex',
    flex: 1,
  },
  note: {
    margin: 0,
    paddingInline: spacer.spacer3,
  },
  input: {
    ...stylex.env.mixin.fpl.typography.textBodyLarge,
    '::placeholder': {
      color: color.textTertiary,
    },
    backgroundColor: 'transparent',
    borderStyle: 'none',
    borderWidth: 0,
    boxSizing: 'border-box',
    color: color.text,
    minHeight: '80px',
    outline: 'none',
    paddingBlock: spacer.spacer25,
    paddingInline: spacer.spacer3,
    resize: 'none',
    width: '100%',
  },
});
